
import { defineComponent, ref, computed, watch } from 'vue';
import { message } from 'ant-design-vue';
import { OioModal, OioButton } from '@kunlun/vue-ui-antd';
import { InboxOutlined } from '@ant-design/icons-vue';
import FileUpload from '../../UploadImage/field/common/File.vue';
import { downloadTemplate, createBillingImportTask } from '../api/billingBill';

interface ResultMessage {
  level: string;
  message: string;
}

export default defineComponent({
  name: 'ImportInvoiceContent',
  components: { InboxOutlined, OioModal, OioButton, FileUpload },
  props: {
    visible: { type: Boolean, default: false }
  },
  emits: ['update:visible', 'success'],
  setup(props, { emit }) {
    const importLoading = ref(false);
    const templateLoading = ref(false);
    const fileValue = ref<any>(null);
    const uploadedFile = ref<{ url: string; name?: string } | null>(null);
    const resultMessages = ref<ResultMessage[]>([]);

    const dialogVisible = computed({
      get: () => props.visible,
      set: (val) => emit('update:visible', val)
    });

    const messageColumns = [
      { title: '级别', dataIndex: 'level', key: 'level', width: 80 },
      { title: '信息', dataIndex: 'message', key: 'message' }
    ];

    /** 受管上传 @change：取最后一个文件的 url */
    const handleFileChange = (files: any[]) => {
      const last = files && files.length ? files[files.length - 1] : null;
      if (last?.url) {
        uploadedFile.value = { url: last.url, name: last.name || last.fileName };
        fileValue.value = last;
      }
    };

    const handleFileRemove = () => {
      uploadedFile.value = null;
      fileValue.value = null;
    };

    const handleDownloadTemplate = async () => {
      templateLoading.value = true;
      try {
        const url = await downloadTemplate();
        if (url) {
          window.open(url, '_blank');
        } else {
          message.warning('模板地址获取失败');
        }
      } catch (e: any) {
        message.error(e?.message || '下载模板失败');
      } finally {
        templateLoading.value = false;
      }
    };

    const handleImportConfirm = async () => {
      if (!uploadedFile.value) {
        message.warning('请先选择并上传文件');
        return;
      }
      importLoading.value = true;
      resultMessages.value = [];
      try {
        const result = await createBillingImportTask(uploadedFile.value);
        resultMessages.value = result.messages || [];
        const hasError = resultMessages.value.some((m) => m.level === 'ERROR');
        if (hasError) {
          message.warning('导入完成，但存在错误，请查看导入结果');
        } else {
          message.success('导入成功');
        }
        emit('success');
      } catch (e: any) {
        message.error(e?.message || '导入失败');
      } finally {
        importLoading.value = false;
        dialogVisible.value = false;
      }
    };

    const handleClose = () => {
      dialogVisible.value = false;
    };

    const resetState = () => {
      uploadedFile.value = null;
      fileValue.value = null;
      resultMessages.value = [];
    };

    watch(
      () => props.visible,
      (val) => {
        if (val) resetState();
      }
    );

    return {
      dialogVisible,
      importLoading,
      templateLoading,
      fileValue,
      resultMessages,
      messageColumns,
      handleFileChange,
      handleFileRemove,
      handleDownloadTemplate,
      handleImportConfirm,
      handleClose
    };
  }
});
